<template>
  <div class="max-w-2xl mx-auto">

    <!-- Feedback form -->
    <div v-if="isCancel">
      <div class="rounded-md bg-red-50 mb-10 ">
        <div class="mx-auto max-w-7xl">
          <div class="flex p-4 ">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <div class="text-sm text-red-700">
                <p>Your payment was declined or canceled.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!submitted" class="flex flex-col gap-6 bg-white rounded-xl border border-gray-200 p-6 sm:p-10">

      <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center">I'd like to hear from you.</h1>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-gray-700">How was your experience?</label>
        <div class="flex flex-col sm:flex-row gap-2">
          <button v-for="option in experienceOptions" :key="option.value" type="button"
            @click="experience = option.value; showExperienceWarning = false"
            class="flex-1 px-4 py-3 rounded-md border text-sm font-medium transition-colors" :class="experience === option.value
              ? 'bg-indigo-600 border-indigo-600 text-white'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'">
            {{ option.label }}
          </button>
        </div>
        <p v-if="showExperienceWarning" class="text-red-600 text-xs mt-1">
          Please select an option.
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <label for="tell-us-message" class="text-sm font-semibold text-gray-700">Anything you'd like us to
          know?</label>
        <textarea id="tell-us-message" v-model="message" rows="6"
          class="w-full rounded-md border border-gray-300 p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Tell us anything you'd like us to know"></textarea>
      </div>

      <label class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <input type="checkbox" v-model="requestedContact"
          class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
        I'd like someone from the team to contact me.
      </label>

      <button type="button" @click="submitFeedback" :disabled="submitting"
        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-3 bg-indigo-600 font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
        {{ submitting ? 'Sending...' : 'Send Feedback' }}
      </button>

    </div>

    <!-- Confirmation -->
    <div v-else
      class="flex flex-col items-center text-center gap-4 bg-white rounded-xl border border-gray-200 p-6 sm:p-10">
      <div class="flex-shrink-0 h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
        <svg class="w-7 h-7 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900">Feedback Received</h1>
      <p class="text-gray-700 text-base sm:text-lg">Thank you for sharing your thoughts.</p>
      <p class="text-gray-600 text-sm sm:text-base leading-relaxed">
        We've received your feedback and appreciate you taking the time to let us know about your experience.
      </p>
      <p class="text-gray-500 text-sm">Returning you to your dashboard...</p>

      <a :href="dashboardUrl"
        class="w-full max-w-xs inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-3 bg-indigo-600 font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-sm">
        Go to Dashboard Now
      </a>
    </div>

  </div>
</template>

<script>

import ProtestRequestApi from "../../../../services/api/ProtestRequest.js";

export default {
  name: 'TellUsHere',

  props: {
    dashboardUrl: {
      type: String,
      default: '/user-properties'
    },
    isCancel: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      experienceOptions: [
        { value: 'great', label: 'Great' },
        { value: 'okay', label: 'Okay' },
        { value: 'could_be_better', label: 'Could be better' }
      ],
      experience: null,
      message: '',
      requestedContact: false,
      submitting: false,
      submitted: false,
      showExperienceWarning: false
    }
  },

  methods: {
    submitFeedback() {
      if (this.submitting) {
        return;
      }

      if (!this.experience) {
        this.showExperienceWarning = true;
        return;
      }

      this.submitting = true;

      ProtestRequestApi.storeTellUsHereFeedback({
        experience: this.experience,
        message: this.message,
        requested_contact: this.requestedContact
      }).then(() => {
        this.submitting = false;
        this.submitted = true;

        setTimeout(() => {
          window.location.href = this.dashboardUrl;
        }, 3500);
      }).catch((error) => {
        this.submitting = false;
        window.flash("Something went wrong. Please try again.", "error");
      });
    }
  }
}
</script>
